import * as apiFactory from "./ApiFactory";

import type { Api } from "./Api";
import type { PagedData } from "@/models/PagedData";

export type CaseRetentionInformation =
  | {
      /* This is null only when the case is viewed before the records are uploaded, or the retention service runs */
      caseStartDate: string | null;
      caseStartDateOverride: string | null;
      caseRetentionDuration: string;
      effectiveExpiryDate: string;
      isIndefinite: boolean;
      computedFrom: "brand-default" | "case-level";
    }
  | {
      caseStartDate: string | null;
      caseStartDateOverride: string | null;
      caseRetentionDuration: null;
      effectiveExpiryDate: null;
      isIndefinite: false;
      computedFrom: "none";
    };

export interface UpdateRetentionDurationRequest {
  duration: string | null;
  durationState:
    | "DurationUnchanged"
    | "DurationRemoved"
    | "DurationSetViaDate"
    | "DurationSetViaDuration"
    | "DurationSetViaQuickExtension";
  isIndefinite: boolean;
  reason: string | null;
}

export interface RetentionReasonedRequest {
  reason: string;
}

export interface UndeleteRequest {
  reason: string;
  duration: string;
}

export interface OverrideStartDateRequest {
  startDate: string | null;
}

export type RetentionStatus =
  | { key: "Retained"; isFilterOnly: false }
  | { key: "LegalHold"; isFilterOnly: false }
  | { key: "Expired"; isFilterOnly: false }
  | { key: "DeletionRequested"; isFilterOnly: false }
  | { key: "DeletionRequired"; isFilterOnly: false }
  | { key: "RecoveryPeriod"; isFilterOnly: false }
  | { key: "DeletionInProgress"; isFilterOnly: false }
  | { key: "Indefinite"; isFilterOnly: true }
  | { key: "ExpiresBefore"; isFilterOnly: true };

export interface RetentionStatusResponse {
  status: Extract<RetentionStatus, { isFilterOnly: false }>["key"];
  requestedAt: string;
}

export interface CaseRetentionStatus {
  status: Extract<RetentionStatus, { isFilterOnly: false }>["key"];
  // The time when the case entered the current status
  createdAt: string;
  // The author who triggered the current status
  createdBy: string;
  reason: string | null;
  // The original deletion request time when status is DeletionRequired; null otherwise
  requestedAt: string | null;
  // The original deletion request author when status is DeletionRequired; null otherwise
  requestedBy: string | null;
}

export interface CaseRetentionHistory {
  createdAt: string;
  createdBy: string;
  field: "CaseStartDateOverride" | "RetentionDuration";
  value: string;
  reason: string;
}

export async function getRetentionDuration(
  clientName: string,
  brand: string,
  caseReference: string,
): Promise<CaseRetentionInformation> {
  const client: Api = await apiFactory.WithBearer();
  return await client.getWithPollingSignal<CaseRetentionInformation>([
    "api/Retention",
    clientName,
    brand,
    caseReference,
    "duration",
  ]);
}

export async function updateRetentionDuration(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: UpdateRetentionDurationRequest,
): Promise<CaseRetentionInformation> {
  const client: Api = await apiFactory.WithBearer();
  return await client.put<UpdateRetentionDurationRequest, CaseRetentionInformation>(
    ["api/Retention", clientName, brand, caseReference, "duration"],
    payload,
  );
}

export async function applyLegalHold(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: RetentionReasonedRequest,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<RetentionReasonedRequest, RetentionStatusResponse>(
    ["api/Retention", clientName, brand, caseReference, "legal-hold"],
    payload,
  );
}

export async function cancelLegalHold(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: RetentionReasonedRequest,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<RetentionReasonedRequest, RetentionStatusResponse>(
    ["api/Retention", clientName, brand, caseReference, "legal-hold", "cancel"],
    payload,
  );
}

export async function requestDeletion(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: RetentionReasonedRequest,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<RetentionReasonedRequest, RetentionStatusResponse>(
    ["api/Retention", clientName, brand, caseReference, "deletion-request"],
    payload,
  );
}

export async function approveDeletionRequest(
  clientName: string,
  brand: string,
  caseReference: string,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<void, RetentionStatusResponse>([
    "api/Retention",
    clientName,
    brand,
    caseReference,
    "deletion-request",
    "approve",
  ]);
}

export async function cancelDeletionRequest(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: RetentionReasonedRequest,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<RetentionReasonedRequest, RetentionStatusResponse>(
    ["api/Retention", clientName, brand, caseReference, "deletion-request", "cancel"],
    payload,
  );
}

export async function recoverCase(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: UndeleteRequest,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.post<UndeleteRequest, RetentionStatusResponse>(
    ["api/Retention", clientName, brand, caseReference, "undelete"],
    payload,
  );
}

export async function getRetentionHistory(
  clientName: string,
  brand: string,
  caseReference: string,
  page: number,
  pageSize: number,
): Promise<PagedData<CaseRetentionHistory[]>> {
  const client: Api = await apiFactory.WithBearer();
  return await client.getWithPollingSignal<PagedData<CaseRetentionHistory[]>>([
    "api/Retention",
    clientName,
    brand,
    caseReference,
    "history",
    String(page),
    String(pageSize),
  ]);
}

export async function getRetentionStatus(
  clientName: string,
  brand: string,
  caseReference: string,
): Promise<CaseRetentionStatus> {
  const client: Api = await apiFactory.WithBearer();
  return await client.getWithPollingSignal<CaseRetentionStatus>([
    "api/Retention",
    clientName,
    brand,
    caseReference,
    "status",
  ]);
}

export async function overrideStartDate(
  clientName: string,
  brand: string,
  caseReference: string,
  payload: OverrideStartDateRequest,
): Promise<RetentionStatusResponse> {
  const client: Api = await apiFactory.WithBearer();
  return await client.patch<OverrideStartDateRequest, RetentionStatusResponse>(
    ["api/Retention", clientName, brand, caseReference, "start-date"],
    payload,
  );
}
